import { useSyncExternalStore } from 'react';
import {
  isSaved,
  readHistory,
  readSaved,
  subscribe,
  type HistoryEntry,
} from './libraryStore';

/**
 * The library as an external store.
 *
 * `useSyncExternalStore` for the same reason the theme and the URL use it:
 * `localStorage` IS the source of truth, shared with other tabs, and copying
 * it into state with an effect is the pattern the React rules name outright.
 *
 * Each hook caches its last snapshot and returns it unchanged when the stored
 * JSON has not changed. Without that, `getSnapshot` would parse and return a
 * NEW array every time React called it, which it treats as a change — and
 * that is an infinite render loop, not a slow one.
 */

let historyCache: { json: string; value: HistoryEntry[] } | null = null;

function historySnapshot(): HistoryEntry[] {
  const value = readHistory();
  const json = JSON.stringify(value);
  if (historyCache === null || historyCache.json !== json) {
    historyCache = { json, value };
  }
  return historyCache.value;
}

let savedCache: { json: string; value: string[] } | null = null;

function savedSnapshot(): string[] {
  const value = readSaved();
  const json = JSON.stringify(value);
  if (savedCache === null || savedCache.json !== json) {
    savedCache = { json, value };
  }
  return savedCache.value;
}

const EMPTY_HISTORY: HistoryEntry[] = [];
const EMPTY_SAVED: string[] = [];

export function useHistory(): HistoryEntry[] {
  return useSyncExternalStore(subscribe, historySnapshot, () => EMPTY_HISTORY);
}

export function useSaved(): string[] {
  return useSyncExternalStore(subscribe, savedSnapshot, () => EMPTY_SAVED);
}

export function useIsSaved(khutbahId: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => isSaved(khutbahId),
    () => false,
  );
}
